import { DietGoal, Gender, MealPlanData, UserMealPlan } from 'fitbeat.models';
import { UserProfile } from '../../../entities/user/userprofile';
import { NutritionSerializer } from '../../serialization/nutrition';
import { IMealPlanRepository } from '../interfaces/imealplan-repository';

export class DbMealPlanRepository implements IMealPlanRepository {
    constructor(private mealPlanDb: any) { }

    public async add(mealPlan: MealPlanData): Promise<MealPlanData> {
        const doc = NutritionSerializer.serializeMealPlanData(mealPlan);
        doc._id = mealPlan._id;
        await this.mealPlanDb.put(doc);
        return NutritionSerializer.deserializeMealPlanData(doc);
    }

    public async get(uid: string): Promise<MealPlanData | undefined> {
        try {
            const mealPlan = await this.mealPlanDb.get(uid);
            return NutritionSerializer.deserializeMealPlanData(mealPlan);
        } catch (err) {
            if (err.status === 404) {
                return undefined;
            } else {
                throw err;
            }
        }
    }

    public async update(mealPlan: MealPlanData): Promise<MealPlanData> {
        const doc = NutritionSerializer.serializeMealPlanData(mealPlan);
        await this.mealPlanDb.put(doc);
        return mealPlan;
    }

    public async getMealPlanForSavedPreferencesAndGender(
      dietGoal: string,
      foodPreference: string,
      gender: string,
      organisationMealTag?: string,
    ): Promise<MealPlanData | undefined> {
        const selector: any = {
          gender,
          dietGoal,
          foodPreference,
          userId: {
            $exists: false,
          },
        };
        if (organisationMealTag) {
          selector.organisation = organisationMealTag;
        } else {
          selector.organisation = {
            $exists: false,
          };
        }
        const result = await this.mealPlanDb.find({selector});
        const docs = result.docs;
        const settings = `${gender} ${foodPreference} ${dietGoal} ${organisationMealTag || '' }`;
        if (docs.length > 1 ) {
           throw new Error(`More than one default meal plan defined for ${settings}`);
        }
        if (docs.length === 0) {
          return;
        }
        return NutritionSerializer.deserializeMealPlanData(docs[0]);
      }

    public async getUserMealPlansForUser(user: UserProfile, dietGoal: DietGoal): Promise<UserMealPlan | undefined> {
        const queryGender = user.gender === Gender.Other ? Gender.Female : user.gender;
        try {
            const result = await this.mealPlanDb.find({
                selector: {
                    userId: user.uid,
                    dietGoal: `${dietGoal}`,
                    foodPreference: user.mealPreferences.foodPreference,
                    gender: queryGender,
                },
            });
            const userMealPlan = result.docs[0];
            if (userMealPlan) {
                return Object.assign(new UserMealPlan(), NutritionSerializer.deserializeMealPlanData(userMealPlan));
            }
            return undefined;
        } catch (err) {
            if (err.status === 404) {
                return undefined;
            } else {
                throw err;
            }
        }
    }

    public async remove(mealPlanData: MealPlanData): Promise<MealPlanData> {
        const entry = await this.get(mealPlanData._id);
        return this.mealPlanDb.remove(entry);
    }
}
